import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from "firebase/firestore";
import { auth, db } from "../firebase/config";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0E1117;
    --surface: #161B27;
    --surface2: #1C2333;
    --border: #252D3D;
    --accent: #F5A623;
    --accent-dim: rgba(245,166,35,0.10);
    --blue: #3B82F6;
    --blue-dim: rgba(59,130,246,0.10);
    --green: #22C55E;
    --green-dim: rgba(34,197,94,0.10);
    --red: #EF4444;
    --red-dim: rgba(239,68,68,0.12);
    --text: #F1F5F9;
    --text-sub: #94A3B8;
    --text-muted: #475569;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }

  .login-wrap { min-height: 100vh; display: grid; grid-template-columns: 1fr 480px; background: var(--bg); }

  .login-left {
    background: linear-gradient(135deg, #0E1117 0%, #161B27 50%, #1a1f2e 100%);
    display: flex; flex-direction: column; justify-content: center;
    padding: 60px; position: relative; overflow: hidden; border-right: 1px solid var(--border);
  }
  .login-left::before {
    content: ''; position: absolute; top: -200px; left: -200px;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(245,166,35,0.06) 0%, transparent 70%);
    pointer-events: none;
  }
  .login-left::after {
    content: ''; position: absolute; bottom: -100px; right: -100px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 70%);
    pointer-events: none;
  }
  .brand { display: flex; align-items: center; gap: 14px; margin-bottom: 60px; }
  .brand-icon { width: 48px; height: 48px; background: var(--accent); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 26px; color: #000; }
  .brand-name { font-family: 'Bebas Neue', sans-serif; font-size: 36px; letter-spacing: 4px; }
  .brand-name span { color: var(--accent); }
  .hero-tag { display: inline-flex; align-items: center; gap: 8px; background: var(--accent-dim); border: 1px solid rgba(245,166,35,0.25); border-radius: 20px; padding: 6px 14px; font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--accent); margin-bottom: 28px; }
  .hero-heading { font-family: 'Bebas Neue', sans-serif; font-size: 52px; letter-spacing: 3px; line-height: 1.05; color: var(--text); margin-bottom: 20px; }
  .hero-heading span { color: var(--accent); }
  .hero-sub { font-size: 15px; color: var(--text-muted); line-height: 1.7; max-width: 400px; margin-bottom: 48px; }
  .feature-list { display: flex; flex-direction: column; gap: 14px; }
  .feature-item { display: flex; align-items: center; gap: 12px; font-size: 13px; color: var(--text-sub); }
  .feature-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }

  .login-right { background: var(--surface); display: flex; flex-direction: column; justify-content: center; padding: 48px 44px; overflow-y: auto; }
  .form-logo { display: flex; align-items: center; gap: 10px; margin-bottom: 36px; }
  .form-logo-icon { width: 34px; height: 34px; background: var(--accent); border-radius: 7px; display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 19px; color: #000; }
  .form-logo-text { font-family: 'Bebas Neue', sans-serif; font-size: 26px; letter-spacing: 3px; }
  .form-logo-text span { color: var(--accent); }

  .form-heading { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 2px; margin-bottom: 6px; }
  .form-sub { font-size: 13px; color: var(--text-muted); margin-bottom: 28px; line-height: 1.5; }

  .step-indicator { display: flex; align-items: center; margin-bottom: 28px; }
  .step-dot { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; transition: all 0.2s; flex-shrink: 0; }
  .step-dot.active { background: var(--accent); color: #000; }
  .step-dot.done { background: var(--green); color: #000; }
  .step-dot.pending { background: var(--border); color: var(--text-muted); }
  .step-line { flex: 1; height: 2px; background: var(--border); border-radius: 1px; margin: 0 4px; }
  .step-line.done { background: var(--green); }

  .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .field label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .field input, .field select { background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; padding: 11px 14px; font-size: 14px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; width: 100%; appearance: none; transition: border-color 0.15s; }
  .field input::placeholder { color: var(--text-muted); }
  .field input:focus, .field select:focus { border-color: var(--blue); }
  .field-hint { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .field-hint.green { color: var(--green); }

  .input-wrap { position: relative; }
  .input-wrap input { padding-right: 44px; }
  .eye-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: var(--text-muted); font-size: 16px; padding: 4px; }

  .dealer-status { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; margin-top: -6px; margin-bottom: 14px; font-size: 12px; font-weight: 600; }
  .dealer-status.new { background: var(--blue-dim); color: var(--blue); border: 1px solid rgba(59,130,246,0.3); }
  .dealer-status.existing { background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); }
  .dealer-status.checking { background: var(--surface2); color: var(--text-muted); border: 1px solid var(--border); }

  .role-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 14px; }
  .role-card { border: 1.5px solid var(--border); border-radius: 8px; padding: 12px; cursor: pointer; transition: all 0.15s; background: var(--surface2); }
  .role-card:hover { border-color: var(--text-muted); }
  .role-card.selected { border-color: var(--blue); background: var(--blue-dim); }
  .role-card-title { font-size: 12px; font-weight: 700; color: var(--text); margin-bottom: 3px; }
  .role-card-desc { font-size: 11px; color: var(--text-muted); line-height: 1.4; }

  .btn-primary { width: 100%; padding: 13px; border-radius: 8px; background: var(--accent); color: #000; font-size: 14px; font-weight: 700; font-family: 'DM Sans', sans-serif; border: none; cursor: pointer; transition: opacity 0.15s; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 6px; }
  .btn-primary:hover { opacity: 0.88; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-ghost { width: 100%; padding: 11px; border-radius: 8px; background: transparent; color: var(--text-sub); font-size: 13px; font-weight: 600; font-family: 'DM Sans', sans-serif; border: 1px solid var(--border); cursor: pointer; transition: all 0.15s; margin-top: 8px; }
  .btn-ghost:hover { border-color: var(--text-sub); color: var(--text); }

  .toggle-mode { text-align: center; margin-top: 20px; font-size: 13px; color: var(--text-muted); }
  .toggle-mode a { color: var(--accent); font-weight: 700; cursor: pointer; }

  /* DEMO CARD */
  .demo-card { margin-top: 24px; border: 1px solid rgba(245,166,35,0.25); border-radius: 12px; overflow: hidden; background: rgba(245,166,35,0.03); }
  .demo-card-header { padding: 16px 18px 12px; }
  .demo-card-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--accent); margin-bottom: 4px; }
  .demo-card-title { font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 2px; color: var(--text); margin-bottom: 6px; }
  .demo-card-desc { font-size: 12px; color: var(--text-muted); line-height: 1.5; }
  .demo-credentials { border-top: 1px solid rgba(245,166,35,0.15); border-bottom: 1px solid rgba(245,166,35,0.15); padding: 12px 18px; display: flex; flex-direction: column; gap: 8px; background: rgba(245,166,35,0.04); }
  .demo-cred-row { display: flex; align-items: center; justify-content: space-between; }
  .demo-cred-label { font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: var(--text-muted); }
  .demo-cred-value { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; color: var(--accent); letter-spacing: 0.5px; }
  .demo-card-note { padding: 10px 18px; font-size: 11px; color: var(--text-muted); text-align: center; }

  .error-banner { background: var(--red-dim); border: 1px solid rgba(239,68,68,0.3); border-radius: 8px; padding: 11px 14px; font-size: 12px; color: var(--red); margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }

  .loading-overlay { position: fixed; inset: 0; background: rgba(14,17,23,0.7); display: flex; align-items: center; justify-content: center; z-index: 999; backdrop-filter: blur(4px); }
  .loading-card { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 32px 40px; text-align: center; }
  .loading-spinner-lg { width: 40px; height: 40px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; margin: 0 auto 16px; }
  .loading-text { font-size: 14px; color: var(--text-sub); }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ROLES = [
  { id: "advisor", label: "Service Advisor", desc: "Look up parts & pricing at write-up" },
  { id: "parts_manager", label: "Parts Manager", desc: "Manage catalog, markup & matching" },
  { id: "service_manager", label: "Service Manager", desc: "Reports, team oversight & performance" },
];

const INTERNAL_DOMAIN = "getmobilerepair.com";

export default function Login({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [success, setSuccess] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [dealerCode, setDealerCode] = useState("");
  const [dealerStatus, setDealerStatus] = useState(null);
  const [existingStoreId, setExistingStoreId] = useState(null);
  const [existingStoreName, setExistingStoreName] = useState("");
  const [role, setRole] = useState("");
  const [storeName, setStoreName] = useState("");

  const isInternal = email.toLowerCase().endsWith(`@${INTERNAL_DOMAIN}`);

  const lookupCode = async (code) => {
    if (!code.trim()) return null;
    try {
      const q = query(collection(db, "stores"), where("dealerCode", "==", code.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) {
        setDealerStatus("new");
        setExistingStoreId(null);
        setExistingStoreName("");
        return "new";
      } else {
        setDealerStatus("existing");
        setExistingStoreId(snap.docs[0].id);
        setExistingStoreName(snap.docs[0].data().name || "");
        return "existing";
      }
    } catch (e) {
      console.error("Dealer lookup:", e);
      // On error, treat as new so registration isn't blocked
      setDealerStatus("new");
      return "new";
    }
  };

  // Live lookup as user types (debounced)
  let timer = null;
  const handleCodeInput = (val) => {
    setDealerCode(val);
    setDealerStatus(null);
    setError("");
    clearTimeout(timer);
    if (val.trim().length < 2) { setChecking(false); return; }
    setChecking(true);
    timer = setTimeout(async () => {
      await lookupCode(val);
      setChecking(false);
    }, 700);
  };

  const handleContinueStep1 = async () => {
    setError("");
    if (!dealerCode.trim()) { setError("Please enter a dealer code."); return; }
    let status = dealerStatus;
    if (!status) {
      setLoading(true);
      status = await lookupCode(dealerCode);
      setLoading(false);
    }
    if (status === "new") setStep(3);
    else if (status === "existing") setStep(2);
  };

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, "users", cred.user.uid));
      if (!userDoc.exists()) { setError("Account not found. Please register."); setLoading(false); return; }
      const userData = userDoc.data();
      if (userData.status === "pending") { setSuccess("pending"); setLoading(false); return; }
      if (onLogin) onLogin({ uid: cred.user.uid, ...userData });
    } catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!email.trim()) { setError("Please enter your email."); return; }
    if (!password || password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });

      const isNewStore = dealerStatus === "new";
      const assignedRole = isNewStore ? "admin" : role;
      const autoApprove = isNewStore || isInternal;
      let storeId = existingStoreId;

      if (isNewStore) {
        storeId = cred.user.uid + "_store";
        await setDoc(doc(db, "stores", storeId), {
          name: storeName.trim() || `${name.trim()}'s Dealership`,
          dealerCode: dealerCode.trim().toUpperCase(),
          plan: "trial",
          trialEnds: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
          markupSettings: { best: 30, better: 20, good: 10, globalOverride: false, globalRate: 30 },
          createdAt: serverTimestamp(),
          createdBy: cred.user.uid,
        });
      }

      await setDoc(doc(db, "users", cred.user.uid), {
        name: name.trim(),
        email: email.toLowerCase(),
        role: assignedRole,
        storeId,
        status: autoApprove ? "active" : "pending",
        createdAt: serverTimestamp(),
      });

      setSuccess(autoApprove ? "active" : "pending");
    } catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  };

  const friendlyError = (code) => {
    switch (code) {
      case "auth/email-already-in-use": return "An account with this email already exists. Try signing in.";
      case "auth/invalid-email": return "Please enter a valid email address.";
      case "auth/wrong-password":
      case "auth/invalid-credential": return "Incorrect email or password. Please try again.";
      case "auth/user-not-found": return "No account found with this email.";
      case "auth/weak-password": return "Password must be at least 6 characters.";
      case "auth/too-many-requests": return "Too many attempts. Please wait a moment and try again.";
      default: return "Something went wrong. Please try again.";
    }
  };

  const stepDone = (s) => mode === "register" && step > s;

  if (success === "pending") return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "48px 44px", maxWidth: 440, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 20 }}>⏳</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 2, marginBottom: 12 }}>APPROVAL PENDING</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 28 }}>
            Your request to join <strong style={{ color: "var(--text-sub)" }}>{existingStoreName}</strong> has been sent to your Store Admin for approval.
          </div>
          <button className="btn-ghost" onClick={() => { setSuccess(""); setMode("login"); setStep(1); setError(""); }}>Back to Sign In</button>
        </div>
      </div>
    </>
  );

  if (success === "active") return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "48px 44px", maxWidth: 440, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 20 }}>🎉</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 2, marginBottom: 12 }}>YOU'RE ALL SET</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 28 }}>
            Your account is active. Sign in below to access your dashboard.
          </div>
          <button className="btn-primary" onClick={() => { setSuccess(""); setMode("login"); setStep(1); setError(""); }}>Sign In Now →</button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{styles}</style>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <div className="loading-spinner-lg" />
            <div className="loading-text">{mode === "login" ? "Signing you in..." : "Creating your account..."}</div>
          </div>
        </div>
      )}
      <div className="login-wrap">
        <div className="login-left">
          <div className="brand">
            <div className="brand-icon">L</div>
            <div className="brand-name">LANE<span>IQ</span></div>
          </div>
          <div className="hero-tag">✦ Built for the Service Drive</div>
          <div className="hero-heading">GOOD.<br />BETTER.<br /><span>BEST.</span></div>
          <div className="hero-sub">Give every customer a real choice at write-up. Live pricing, real availability, instant trust.</div>
          <div className="feature-list">
            {["VIN & license plate lookup in seconds","Three-tier pricing — OEM, Premium, Value","Live parts availability from PartsTech","Recall & TSB alerts built in","Per-advisor performance reporting"].map(f => (
              <div key={f} className="feature-item"><div className="feature-dot" />{f}</div>
            ))}
          </div>
        </div>

        <div className="login-right">
          <div className="form-logo">
            <div className="form-logo-icon">L</div>
            <div className="form-logo-text">LANE<span>IQ</span></div>
          </div>

          {mode === "login" ? (
            <>
              <div className="form-heading">Welcome Back</div>
              <div className="form-sub">Sign in to your store dashboard</div>
              {error && <div className="error-banner">⚠ {error}</div>}
              <div className="field">
                <label>Email Address</label>
                <input type="email" placeholder="you@dealership.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} />
              </div>
              <div className="field">
                <label>Password</label>
                <div className="input-wrap">
                  <input type={showPass ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} />
                  <button className="eye-btn" onClick={() => setShowPass(!showPass)}>{showPass ? "🙈" : "👁"}</button>
                </div>
              </div>
              <button className="btn-primary" onClick={handleLogin} disabled={loading}>Sign In →</button>
              <div className="toggle-mode">
                Don't have an account? <a onClick={() => { setMode("register"); setStep(1); setError(""); }}>Register your store</a>
              </div>

              {/* DEMO ACCESS CARD */}
              <div className="demo-card">
                <div className="demo-card-header">
                  <div className="demo-card-eyebrow">🔍 Exploring LaneIQ?</div>
                  <div className="demo-card-title">Try the Demo</div>
                  <div className="demo-card-desc">
                    Full access to Summit Auto Group — a pre-loaded demo store with real vehicle lookups, advisor reporting, and parts pricing.
                  </div>
                </div>
                <div className="demo-credentials">
                  <div className="demo-cred-row">
                    <div className="demo-cred-label">Email</div>
                    <div className="demo-cred-value">demo@laneiq.io</div>
                  </div>
                  <div className="demo-cred-row">
                    <div className="demo-cred-label">Password</div>
                    <div className="demo-cred-value">LaneIQ2026</div>
                  </div>
                </div>
                <div className="demo-card-note">
                  Demo data resets nightly · No credit card required
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="form-heading">
                {step === 1 ? "Find Your Store" : step === 2 ? "Your Role" : "Create Account"}
              </div>
              <div className="form-sub">
                {step === 1 ? "Enter your dealer code — or create a new one" : step === 2 ? "What best describes your position?" : "Almost done — set up your login"}
              </div>

              <div className="step-indicator">
                <div className={`step-dot ${step === 1 ? "active" : stepDone(1) ? "done" : "pending"}`}>{stepDone(1) ? "✓" : "1"}</div>
                <div className={`step-line ${stepDone(1) ? "done" : ""}`} />
                <div className={`step-dot ${step === 2 ? "active" : stepDone(2) ? "done" : "pending"}`}>{stepDone(2) ? "✓" : "2"}</div>
                <div className={`step-line ${stepDone(2) ? "done" : ""}`} />
                <div className={`step-dot ${step === 3 ? "active" : stepDone(3) ? "done" : "pending"}`}>{stepDone(3) ? "✓" : "3"}</div>
              </div>

              {error && <div className="error-banner">⚠ {error}</div>}

              {step === 1 && (
                <>
                  <div className="field">
                    <label>Dealer Code</label>
                    <input
                      placeholder="Any format — numbers, letters, or both"
                      value={dealerCode}
                      onChange={e => handleCodeInput(e.target.value)}
                      style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}
                    />
                    <div className="field-hint">Use your manufacturer's dealer code, DMS code, or create your own</div>
                  </div>
                  {checking && <div className="dealer-status checking">🔍 Looking up dealer code...</div>}
                  {!checking && dealerStatus === "new" && (
                    <>
                      <div className="dealer-status new">✦ New store — you'll be set up as Store Admin</div>
                      <div className="field">
                        <label>Store / Dealership Name</label>
                        <input placeholder="e.g. Toyota of Orlando" value={storeName} onChange={e => setStoreName(e.target.value)} />
                      </div>
                    </>
                  )}
                  {!checking && dealerStatus === "existing" && (
                    <div className="dealer-status existing">✓ Found: <strong style={{ marginLeft: 4 }}>{existingStoreName}</strong></div>
                  )}
                  <button className="btn-primary" onClick={handleContinueStep1}>Continue →</button>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="role-grid">
                    {ROLES.map(r => (
                      <div key={r.id} className={`role-card ${role === r.id ? "selected" : ""}`} onClick={() => setRole(r.id)}>
                        <div className="role-card-title">{r.label}</div>
                        <div className="role-card-desc">{r.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.5 }}>
                    Your Store Admin will confirm your role before you can access the system.
                  </div>
                  <button className="btn-primary" onClick={() => { if (!role) { setError("Please select your role."); return; } setError(""); setStep(3); }}>Continue →</button>
                  <button className="btn-ghost" onClick={() => { setStep(1); setError(""); }}>← Back</button>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="field">
                    <label>Full Name</label>
                    <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Email Address</label>
                    <input type="email" placeholder="you@dealership.com" value={email} onChange={e => setEmail(e.target.value)} />
                    {isInternal && <div className="field-hint green">✓ Internal account — auto-approved</div>}
                  </div>
                  <div className="field">
                    <label>Password</label>
                    <div className="input-wrap">
                      <input type={showPass ? "text" : "password"} placeholder="Min. 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
                      <button className="eye-btn" onClick={() => setShowPass(!showPass)}>{showPass ? "🙈" : "👁"}</button>
                    </div>
                  </div>
                  <button className="btn-primary" onClick={handleRegister} disabled={loading}>Create Account →</button>
                  <button className="btn-ghost" onClick={() => { setStep(dealerStatus === "new" ? 1 : 2); setError(""); }}>← Back</button>
                </>
              )}

              <div className="toggle-mode">
                Already have an account? <a onClick={() => { setMode("login"); setError(""); }}>Sign in</a>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
